import { useQuery } from "@apollo/client";
import {
  AccountCard,
  BreadcrumbArray,
  Card,
  CurrencyAmountUI,
  PrimaryFullScreenSpinner,
  Txt,
} from "@repo/ui";
import {
  accountIsAsset,
  accountIsBeneficiaryAccount,
  NotFoundError,
} from "@repo/utils";
import { createFileRoute, Link, useParams } from "@tanstack/react-router";
import "@xyflow/react/dist/style.css";
import {
  Background,
  BackgroundVariant,
  Controls,
  type Edge,
  Handle,
  MarkerType,
  type Node,
  type NodeProps,
  type NodeTypes,
  Position,
  ReactFlow,
  ReactFlowProvider,
} from "@xyflow/react";
import { createContext, Suspense, useContext, useMemo, useState } from "react";
import { AssetRuleCard } from "~/components/Asset/AssetRulesCard";
import { AdminPageContainer } from "~/components/layout/AdminPageContainer";
import { AdminPageHeader } from "~/components/layout/AdminPageHeader";
import { graphql } from "~/gql/client";
import type {
  AdminOrgRulesGraphQuery,
  AssetRuleCardFieldsFragment,
} from "~/gql/client/graphql";
import { opsHead, useOpsTitle } from "~/head";

export const Route = createFileRoute(
  "/__auth/organisations_/$organisationId_/rules",
)({
  head: () => opsHead("Rules"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

type OrgAccount = NonNullable<
  NonNullable<
    NonNullable<AdminOrgRulesGraphQuery["organisation"]>["accountsSummary"]
  >["accounts"]
>[number];

type HandleSpec = {
  id: string;
  type: "source" | "target";
  top: number;
  color: string;
};

type RuleEdgeData = { ruleId: string };

type AccountNodeType = Node<
  { account: OrgAccount; handles: HandleSpec[] },
  "account"
>;

type TreasuryNodeType = Node<{ handles: HandleSpec[] }, "treasury">;

const TREASURY_NODE_ID = "primary-treasury";

const NODE_WIDTH = 700;

const TOPUP_COLOR = "hsl(var(--color-info-600))";
const SWEEP_COLOR = "hsl(var(--color-warning-600))";

const HoveredRuleContext = createContext<{
  hoveredRuleId: string | null;
  setHoveredRuleId: (id: string | null) => void;
}>({ hoveredRuleId: null, setHoveredRuleId: () => {} });

function AccountNode({ data }: NodeProps<AccountNodeType>) {
  const { account } = data;
  const { hoveredRuleId, setHoveredRuleId } = useContext(HoveredRuleContext);
  const isAsset = accountIsAsset(account);
  const isBeneficiary = accountIsBeneficiaryAccount(account);
  const rules: AssetRuleCardFieldsFragment[] =
    isAsset || isBeneficiary ? (account.assetRules ?? []) : [];

  return (
    <div
      className="flex flex-col gap-2 rounded-lg border bg-primary p-2 shadow-card"
      style={{ width: NODE_WIDTH }}
    >
      {data.handles.map((h) => (
        <Handle
          key={h.id}
          type={h.type}
          position={Position.Right}
          id={h.id}
          style={{
            top: h.top,
            background: h.color,
            border: "2px solid white",
            width: 10,
            height: 10,
          }}
        />
      ))}

      <div
        onMouseDown={(e) => e.stopPropagation()}
        onWheelCapture={(e) => e.stopPropagation()}
      >
        {isAsset && (
          <div className="flex justify-between">
            <div className="max-w-lg">
              <AccountCard account={account} subText={["BUSINESS_ENTITY"]} />
            </div>
            <div className="flex max-w-[20rem] flex-col items-end">
              <div className="flex items-center gap-2">
                <Txt variant="field-label">Actual balance</Txt>
                <CurrencyAmountUI ca={account.estimatedValue} />
              </div>
              <div className="flex items-center gap-2">
                <Txt variant="field-label">Projected balance</Txt>
                <CurrencyAmountUI
                  ca={account.estimatedValueWithOpenFundMovements}
                />
              </div>
            </div>
          </div>
        )}
        {isBeneficiary && (
          <AccountCard
            account={account}
            subText={["BUSINESS_ENTITY", "BALANCE_FULL"]}
          />
        )}

        {rules.length > 0 && (
          <div className="flex flex-col gap-2 pt-2">
            {rules.map((rule) => (
              <div
                key={rule.id}
                onMouseEnter={() => setHoveredRuleId(rule.id)}
                onMouseLeave={() => setHoveredRuleId(null)}
                className={`rounded-md transition-shadow ${rule.id === hoveredRuleId ? "ring-2 ring-info-active" : ""}`}
              >
                <AssetRuleCard rule={rule} />
                {(rule.__typename === "TopUpAssetRule" ||
                  rule.__typename === "SweepAssetRule") &&
                  rule.openTransferGroup && (
                    <div className="mt-1 px-2 pb-2">
                      <Link
                        to="/transfer-groups/$transferGroupId"
                        params={{
                          transferGroupId: rule.openTransferGroup.id,
                        }}
                        className="inline-flex rounded-md border bg-secondary px-2 py-1 text-info text-xs hover:bg-secondary-interactive-hover"
                      >
                        Open fund movement: {rule.openTransferGroup.shortRef} (
                        {rule.openTransferGroup.status})
                      </Link>
                    </div>
                  )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function TreasuryNode({ data }: NodeProps<TreasuryNodeType>) {
  return (
    <Card style={{ width: NODE_WIDTH / 2 }} className="p-4 text-center">
      {data.handles.map((h) => (
        <Handle
          key={h.id}
          type={h.type}
          position={Position.Bottom}
          id={h.id}
          style={{
            background: h.color,
            border: "2px solid white",
            width: 10,
            height: 10,
          }}
        />
      ))}
      <Txt variant="card-title">Primary Treasury</Txt>
    </Card>
  );
}

const nodeTypes: NodeTypes = { account: AccountNode, treasury: TreasuryNode };

function buildLayout(accounts: OrgAccount[]): {
  nodes: (AccountNodeType | TreasuryNodeType)[];
  edges: Edge<RuleEdgeData>[];
} {
  const visibleAccounts = accounts.filter(
    (a) => accountIsAsset(a) || accountIsBeneficiaryAccount(a),
  );

  // Show all accounts that are involved with an asset rule (has an asset rule, or is a source/destination of an
  // asset rule).
  const involvedAccountIds = new Set<string>();
  for (const account of visibleAccounts) {
    const rules = account.assetRules ?? [];
    if (rules.length > 0) {
      involvedAccountIds.add(account.id);
    }
    for (const rule of rules) {
      if (rule.__typename === "TopUpAssetRule" && rule.sourceAssets) {
        for (const src of rule.sourceAssets) {
          involvedAccountIds.add(src.id);
        }
      }
      if (rule.__typename === "SweepAssetRule") {
        if (rule.destinationAsset?.id) {
          involvedAccountIds.add(rule.destinationAsset.id);
        }
        if (rule.destinationBeneficiaryAccount?.id) {
          involvedAccountIds.add(rule.destinationBeneficiaryAccount.id);
        }
      }
    }
  }

  const involvedAccounts = visibleAccounts.filter((a) =>
    involvedAccountIds.has(a.id),
  );
  // nodeIds is used during edge construction to skip edges that point outside the visible graph.
  const nodeIds = new Set(involvedAccounts.map((a) => a.id));

  // Edges connect two nodes via named handles. Each node renders a set of <Handle>
  // components; an edge's sourceHandle/targetHandle must match a Handle's `id` prop
  // on the corresponding node. Without explicit handle IDs the edge attaches to the
  // node's default centre point and all edges pile up on top of each other.
  //
  // We allocate handles sequentially per node as we build edges, so each edge gets
  // its own dedicated handle on both ends, spaced 20px apart top-to-bottom.
  //
  // TopUp: funds flow FROM a source asset TO the account that holds the rule.
  // Sweep: funds flow FROM the account that holds the rule TO a destination.
  const nodeHandles = new Map<string, HandleSpec[]>();

  const allocateHandle = (
    nodeId: string,
    type: "source" | "target",
    color: string,
  ): string => {
    const handles = nodeHandles.get(nodeId) ?? [];
    const id = `${nodeId}-h${handles.length}`;
    handles.push({ id, type, top: 20 + handles.length * 20, color });
    nodeHandles.set(nodeId, handles);
    return id;
  };

  const edges: Edge<RuleEdgeData>[] = [];

  const hasTopUpRules = involvedAccounts.some((a) =>
    (a.assetRules ?? []).some((r) => r.__typename === "TopUpAssetRule"),
  );

  // Single shared handle on the Treasury node — all TopUp edges originate from
  // the same bottom-center point, so one handle is sufficient.
  const TREASURY_HANDLE_ID = `${TREASURY_NODE_ID}-source`;
  if (hasTopUpRules) {
    nodeHandles.set(TREASURY_NODE_ID, [
      { id: TREASURY_HANDLE_ID, type: "source", top: 0, color: TOPUP_COLOR },
    ]);
  }

  for (const account of involvedAccounts) {
    for (const rule of account.assetRules ?? []) {
      if (rule.__typename === "TopUpAssetRule" && rule.sourceAssets) {
        const sourceAssets = rule.sourceAssets.filter((src) =>
          nodeIds.has(src.id),
        );
        for (const [i, src] of sourceAssets.entries()) {
          edges.push({
            id: `topup-${rule.id}-${src.id}`,
            source: src.id,
            sourceHandle: allocateHandle(src.id, "source", TOPUP_COLOR),
            target: account.id,
            targetHandle: allocateHandle(account.id, "target", TOPUP_COLOR),
            // smoothstep draws right-angle paths with rounded corners — cleaner than
            // bezier when nodes are arranged in a grid.
            type: "smoothstep",
            markerEnd: { type: MarkerType.ArrowClosed, color: TOPUP_COLOR },
            style: { stroke: TOPUP_COLOR, strokeWidth: 2 },
            label: `Priority #${i + 1}`,
            labelBgStyle: { fill: "hsl(var(--color-info-50))" },
            labelStyle: { fill: TOPUP_COLOR, fontSize: 11, fontWeight: 500 },
            data: { ruleId: rule.id },
          });
        }
        // Primary Treasury is always the last-resort source for every TopUp rule.
        edges.push({
          id: `topup-treasury-${rule.id}`,
          source: TREASURY_NODE_ID,
          sourceHandle: TREASURY_HANDLE_ID,
          target: account.id,
          targetHandle: allocateHandle(account.id, "target", TOPUP_COLOR),
          type: "smoothstep",
          markerEnd: { type: MarkerType.ArrowClosed, color: TOPUP_COLOR },
          style: { stroke: TOPUP_COLOR, strokeWidth: 2 },
          label: `Priority #${sourceAssets.length + 1}`,
          labelBgStyle: { fill: "hsl(var(--color-info-50))" },
          labelStyle: { fill: TOPUP_COLOR, fontSize: 11, fontWeight: 500 },
          data: { ruleId: rule.id },
        });
      }

      if (rule.__typename === "SweepAssetRule") {
        const destId =
          rule.destinationAsset?.id ?? rule.destinationBeneficiaryAccount?.id;
        if (destId && nodeIds.has(destId)) {
          edges.push({
            id: `sweep-${rule.id}`,
            source: account.id,
            sourceHandle: allocateHandle(account.id, "source", SWEEP_COLOR),
            target: destId,
            targetHandle: allocateHandle(destId, "target", SWEEP_COLOR),
            type: "smoothstep",
            markerEnd: { type: MarkerType.ArrowClosed, color: SWEEP_COLOR },
            style: { stroke: SWEEP_COLOR, strokeWidth: 2 },
            data: { ruleId: rule.id },
          });
        }
      }
    }
  }

  // Each xyflow Node needs an absolute { x, y } position in canvas-space pixels.
  // Nodes are laid out diagonally: each successive node steps one column to the
  // right and VERTICAL_STEP px down, giving a top-left → bottom-right cascade.
  // The Primary Treasury node sits at the top-right, outside the cascade.
  const COLUMN_GAP = 80;
  const VERTICAL_STEP = 260;
  const ACCOUNT_START_Y = 120;

  const accountNodes: AccountNodeType[] = involvedAccounts.map(
    (account, i) => ({
      id: account.id,
      // "account" maps to the AccountNode component registered in nodeTypes below.
      type: "account",
      position: {
        x: i * (NODE_WIDTH / 2 + COLUMN_GAP),
        y: ACCOUNT_START_Y + i * VERTICAL_STEP,
      },
      // data is passed as props to the node component.
      data: { account, handles: nodeHandles.get(account.id) ?? [] },
    }),
  );

  const nodes: (AccountNodeType | TreasuryNodeType)[] = hasTopUpRules
    ? [
        {
          id: TREASURY_NODE_ID,
          type: "treasury",
          position: {
            x: involvedAccounts.length * (NODE_WIDTH / 2 + COLUMN_GAP),
            y: 0,
          },
          data: { handles: nodeHandles.get(TREASURY_NODE_ID) ?? [] },
        },
        ...accountNodes,
      ]
    : accountNodes;

  return { nodes, edges };
}

function View() {
  const { organisationId } = useParams({
    from: "/__auth/organisations_/$organisationId_/rules",
  });

  if (!organisationId) {
    throw new NotFoundError();
  }

  const { data, loading } = useQuery(
    graphql(`
      query AdminOrgRulesGraph($organisationId: ID!) {
        organisation(id: $organisationId) {
          id
          name
          accountsSummary {
            id
            accounts {
              ... on Asset {
                id
                ...AssetCardFields
                assetRules {
                  ...AssetRuleCardFields
                }
                estimatedValue {
                  amount
                  currency
                }
                estimatedValueWithOpenFundMovements {
                  amount
                  currency
                }
              }
              ... on BeneficiaryAccount {
                id
                assetRules {
                  ...AssetRuleCardFields
                }
                ...BeneficiaryAssetCardFields
              }
            }
          }
        }
      }
    `),
    {
      variables: { organisationId },
    },
  );

  const organisation = data?.organisation;
  const accounts = organisation?.accountsSummary?.accounts ?? [];

  useOpsTitle(organisation?.name ? `${organisation.name} — Rules` : "Rules");

  const [hoveredRuleId, setHoveredRuleId] = useState<string | null>(null);
  const { nodes, edges: baseEdges } = useMemo(
    () => buildLayout(accounts),
    [accounts],
  );

  const edges = useMemo(
    () =>
      baseEdges.map((e) => {
        const isMatch =
          hoveredRuleId !== null && e.data?.ruleId === hoveredRuleId;
        let opacity: number;
        if (hoveredRuleId === null) {
          opacity = 0.35;
        } else if (isMatch) {
          opacity = 1;
        } else {
          opacity = 0.15;
        }
        return { ...e, style: { ...e.style, opacity } };
      }),
    [baseEdges, hoveredRuleId],
  );

  if (loading) {
    return <PrimaryFullScreenSpinner />;
  }
  if (!organisation) {
    throw new NotFoundError();
  }

  return (
    <AdminPageContainer fullWidth>
      <BreadcrumbArray
        stack={[
          { to: "/organisations", title: "Organisations" },
          { to: `/organisations/${organisationId}`, title: organisation.name },
          { title: "Rules" },
        ]}
      />
      <AdminPageHeader title="Rules visualisation" />

      {accounts.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-24 text-secondary">
          <p className="text-sm">No accounts in this organisation.</p>
        </div>
      ) : (
        <HoveredRuleContext.Provider
          value={{ hoveredRuleId, setHoveredRuleId }}
        >
          <ReactFlowProvider>
            <div style={{ height: "calc(100vh - 320px)" }}>
              <ReactFlow
                defaultNodes={nodes}
                edges={edges}
                nodeTypes={nodeTypes}
                fitView
                fitViewOptions={{ padding: 0.15, maxZoom: 1 }}
                nodesConnectable={false}
                deleteKeyCode={null}
                onEdgeMouseEnter={(_event, edge) => {
                  if (edge.data?.ruleId) {
                    setHoveredRuleId(edge.data.ruleId);
                  }
                }}
                onEdgeMouseLeave={() => setHoveredRuleId(null)}
              >
                <Background
                  variant={BackgroundVariant.Dots}
                  gap={20}
                  size={1}
                  color="hsl(var(--color-neutral-300))"
                />
                <Controls />
              </ReactFlow>
            </div>
          </ReactFlowProvider>
        </HoveredRuleContext.Provider>
      )}
    </AdminPageContainer>
  );
}
